import React, { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { groupByInitial } from '../contactName';
import ContactCard from '../ContactCard';

/**
 * Card view of a contact list: one section per initial (A, B, …, #) with a
 * responsive grid of cards, and infinite scroll — the next page loads when
 * the end of the list scrolls into view. `contacts` must come sorted by name,
 * otherwise the sections make no sense (the page then shows them without
 * letters via `grouped={false}`).
 *
 * `getCardProps(contact)` → { to, actions } for each ContactCard.
 */
export default function ContactCardGroups({
  contacts,
  grouped = true,
  getCardProps,
  hasNextPage,
  isFetchingNextPage,
  onFetchNextPage,
  sx,
}) {
  const sentinel = useRef(null);

  useEffect(() => {
    const target = sentinel.current;
    if (!target) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) onFetchNextPage();
      },
      { rootMargin: '200px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onFetchNextPage]);

  const groups = grouped ? groupByInitial(contacts) : [{ letter: null, items: contacts }];

  return (
    <Stack spacing={3} sx={sx}>
      {groups.map((group) => (
        <Stack key={group.letter ?? 'all'} spacing={2}>
          {group.letter && (
            <Stack spacing={1}>
              <Typography variant="subtitle1" color="primary" fontWeight="fontWeightBold">
                {group.letter}
              </Typography>
              <Divider />
            </Stack>
          )}
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            }}
          >
            {group.items.map((contact) => (
              <ContactCard key={contact.uuid} contact={contact} {...getCardProps?.(contact)} />
            ))}
          </Box>
        </Stack>
      ))}
      {hasNextPage && (
        <Stack ref={sentinel} alignItems="center" sx={{ py: 2 }}>
          {isFetchingNextPage && <CircularProgress size={24} />}
        </Stack>
      )}
    </Stack>
  );
}
